import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {Image, PlainDetails, SectionLanguage} from "../../../types/admin";
import {addSection, changeGameDetail, updateSection} from "../adminGameView";
import {ModalState} from "../../../types/modals";
import {toastSuccess} from "../admin";


export type SectionEditModalState = ModalState & {
    languageTab: string
    section_id: string | null
    languages: SectionLanguage[]
    theme: PlainDetails | null
    theme_options: PlainDetails[]
    background_image: Image | null
    isUploading: boolean
}

const initialState: SectionEditModalState = {
    loading: false,
    isOpen: false,
    languageTab: '',
    section_id: null,
    error: null,
    theme: null,
    languages: [],
    theme_options: [],
    background_image: null,
    isUploading: false
}

type SectionGetPayload = {
    section_id: string
    game_id: string
    version_id: string
}



export const uploadSectionImage = createAsyncThunk(
    'sectionEditModal/uploadSectionImage',
    async (payload: FormData, thunkAPI) => {
        const section_id = payload.get('section_id');
        if (section_id === null) {
            return thunkAPI.rejectWithValue('Section id is missing.');
        }
        try {
            return await api.post(`/API/admin/sections/${section_id}/image/`, payload, true);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const getSection = createAsyncThunk(
    'sectionEditModal/getSection',
    async (payload: SectionGetPayload, thunkAPI) => {
        const {section_id, game_id, version_id} = payload;
        try{
            return await api.get(`/API/admin/games/${game_id}/versions/${version_id}/sections/${section_id}/`);
        }catch (error: any){
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewSection = createAsyncThunk(
    'sectionEditModal/saveNewSection',
    async (payload: FormData, thunkAPI) => {
        const game_id = payload.get('game_id');
        const version_id = payload.get('version_id');
        const dispatch = thunkAPI.dispatch;
        try{
            const response = await api.post(`/API/admin/games/${game_id}/versions/${version_id}/sections/`, payload, true);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            dispatch(addSection(response));
            return response
        }catch (error: any){
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const saveSection = createAsyncThunk(
    'sectionEditModal/saveSection',
    async (payload: FormData, thunkAPI) => {
        const game_id = payload.get('game_id');
        const version_id = payload.get('version_id');
        const section_id = payload.get('section_id');
        const dispatch = thunkAPI.dispatch;
        try{
            const response = await api.patch(`/API/admin/games/${game_id}/versions/${version_id}/sections/${section_id}/`, payload, true);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            dispatch(updateSection(response));
            return response

        }catch (error: any){
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const deleteSection = createAsyncThunk(
    'sectionEditModal/deleteQuestion',
    async (payload: SectionGetPayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const {game_id, version_id, section_id} = payload;
        try {
            const response = await api.delete(`/API/admin/games/${game_id}/versions/${version_id}/sections/${section_id}/`);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            dispatch(changeGameDetail({version_sections: response.version_sections}));
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type SectionModalPayload = {
    section_id: string | null
    language: string
}

const slice = createSlice({
    name: 'sectionEditModal',
    initialState,
    reducers: {
        setActiveLanguage: (state, action: PayloadAction<string>) => {
            state.languageTab = action.payload;
        },
        openSectionModal: (state, action: PayloadAction<SectionModalPayload>) => {
            if (action.payload.section_id) {
                state.section_id = action.payload.section_id;
            }else{
                state.section_id = null;
            }

            state.languageTab = action.payload.language;

            state.isOpen = true;
        },
        closeSectionModal: () => {
            return initialState;
        },
        changeSectionModal: (state, action: PayloadAction<Partial<SectionEditModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadSectionImage.pending, (state) => {
                state.isUploading = true
            })
            .addCase(uploadSectionImage.fulfilled, (state, action) => {
                state.isUploading = false
                state.background_image = action.payload.background_image;
            })
            .addCase(uploadSectionImage.rejected, (state, action) => {
                state.isUploading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewSection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewSection.fulfilled, (state, action) => {
                return initialState;
            })
            .addCase(saveNewSection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveSection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveSection.fulfilled, (state, action) => {
                return initialState;
            })
            .addCase(saveSection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getSection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSection.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getSection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteSection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSection.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteSection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });

    }
});

export const {
    openSectionModal,
    closeSectionModal,
    changeSectionModal,
    setActiveLanguage,
} = slice.actions;
export default slice.reducer;